import { useCallback, useRef, useState } from 'react';
import store from 'Store';
import { completeAuthentication as completeAuthAction } from 'actions/AuthActions';
import { useReturnUrl } from 'pages/Auth/SignIn/hooks/useReturnUrl';
import Url from 'utils/Url';

/**
 * Hook that owns which auth screen the SignIn page is currently showing AND the
 * authentication completion for the new /signin flow (SIM-10 / ACS-4134).
 *
 * State machine (SIM-1): { currentScreen, goTo }. Known screens: 'passkey-detection'
 * (initial), 'credential' (SIM-2), 'authorization'/'otp' (SIM-6..9). goTo(screen)
 * accepts any key so sibling stories add screens without changing the signature.
 *
 * Completion orchestration (SIM-10, ADR-SIM10-002): completeAuthentication dispatches the
 * AuthActions.completeAuthentication DISPATCHER (which routes OTP -> verifyOTP +
 * signInSuccessAfterMFA, password -> signIn), then on success redirects to the validated
 * returnUrl STRICTLY AFTER the session is stored. On failure it never redirects; it routes
 * authError:
 *   - ERR_OTP_LOGIN_SESSION_ERROR / session-timeout -> authError 'sessionExpired',
 *     clear state (goTo 'credential') and redirect to /signin (AC-028)
 *   - any other error (incl. backend 5xx) -> authError 'genericError', no auto-retry,
 *     no redirect (AC-034)
 * The error is rethrown so useSubmitGuard can reset its in-flight state.
 *
 * The dispatch goes through the Store singleton (not useDispatch) because Auth hooks run
 * outside a react-redux Provider in their tests — the established Auth pattern.
 */
const INITIAL_SCREEN = 'passkey-detection';
const SESSION_ERROR_CODE = 'ERR_OTP_LOGIN_SESSION_ERROR';
const SIGNIN_CLEARED_PATH = '/signin';

const useAuthFlow = () => {
    const [currentScreen, setCurrentScreen] = useState(INITIAL_SCREEN);
    const [authError, setAuthError] = useState(null);
    // Additive (SIM-7 / ACS-4131, F-002): the lookup result carried forward by
    // CredentialEntryScreen so AuthorizationScreen/OtpEntryScreen have their inputs
    // without a re-lookup. Existing consumers are unaffected — purely added to the
    // returned object.
    const [authContext, setAuthContext] = useState(null);
    const goTo = useCallback(screen => setCurrentScreen(screen), []);

    // `|| {}` is load-bearing: the SIM-1 HK-1 test path renders this hook without mocking
    // useReturnUrl (auto-mock returns undefined), so guard the destructure. Not dead code.
    const { redirectToReturnUrl } = useReturnUrl() || {};
    // Keep the latest redirect callback without adding it to completeAuthentication's deps.
    const redirectRef = useRef(redirectToReturnUrl);
    redirectRef.current = redirectToReturnUrl;

    const completeAuthentication = useCallback(async params => {
        setAuthError(null);

        let session;

        try {
            // Dispatcher routes by method and completes token handling internally.
            session = await store.dispatch(completeAuthAction(params));
        } catch (error) {
            if (error && error.errorCode === SESSION_ERROR_CODE) {
                // Session expired/invalid: clear in-flight flow state and bounce to
                // /signin (which re-evaluates the SIM-1 two-layer gate on landing).
                setAuthError('sessionExpired');
                setCurrentScreen('credential');
                Url.redirectTo(SIGNIN_CLEARED_PATH);
            } else {
                // Backend 5xx / generic: show the generic message, no auto-retry, no redirect.
                setAuthError('genericError');
            }

            // Rethrow so useSubmitGuard resets and the caller can react.
            throw error;
        }

        // Redirect to the validated returnUrl STRICTLY AFTER the session is established.
        // Guarded: useReturnUrl() is destructured with `|| {}`, so redirectToReturnUrl can be
        // undefined (e.g. the hook renders outside a mounted useReturnUrl); a missing redirect
        // must not throw and turn a successful auth into a crash. (Supersedes the Copilot
        // Autofix `typeof` guard with equivalent optional chaining.)
        redirectRef.current?.();

        return session;
    }, []);

    return {
        currentScreen,
        goTo,
        completeAuthentication,
        authError,
        authContext,
        setAuthContext
    };
};

export default useAuthFlow;
